import { Capacitor } from '@capacitor/core'
import { useApiAuthActions } from '@cretesuite/api-client-auth'
import { useIsAuthenticated } from '@cretesuite/api-client-auth/src/hooks/useIsAuthenticated'
import OneSignal from 'onesignal-cordova-plugin'
import React, { useCallback, useContext, useMemo } from 'react'
import { useGraphQLCache } from '../context/GraphQLProvider/Cache'
import { API_CLIENT_ID } from '../data/environment'
import { getTokenClaims } from '../helpers/authToken'

export type SessionToken = string | null

export type LoginResult = {
  success: boolean
  /** True when the operator belongs to more than one organization. */
  multiOrg?: boolean
  error?: Error
}

export interface Session {
  loggedIn: boolean
  logout: () => Promise<void>
  login: (idJwtToken: string) => Promise<LoginResult>
}

const initialContext: Session = {
  loggedIn: false,
  logout: () => Promise.resolve(),
  login: (_) => Promise.resolve({ success: false }),
}

export const SessionContext = React.createContext(initialContext)

export const useSession = () => useContext(SessionContext)

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { clear: clearApiAuth, authenticate } = useApiAuthActions()
  const { clear: clearGraphQLCache } = useGraphQLCache()
  const isAuthenticated = useIsAuthenticated()

  const login = useCallback(
    async (idJwtToken: string): Promise<LoginResult> => {
      try {
        const accessToken = await authenticate({
          data: {
            clientId: API_CLIENT_ID,
            jwt: idJwtToken,
          },
        })
        if (!accessToken) {
          throw new Error('Could not authenticate')
        }
        return {
          success: true,
          multiOrg: getTokenClaims(accessToken).tids.length > 1,
        }
      } catch (error) {
        return {
          success: false,
          error: error as Error,
        }
      }
    },
    [authenticate],
  )

  const logout = useCallback(async () => {
    // Preserve user preferences across logout
    const locale = localStorage.getItem('crew-app-locale')
    const navApp = localStorage.getItem('crew-nav-app')

    clearApiAuth()
    localStorage.clear()
    sessionStorage.clear()

    // Restore preferences
    if (locale) localStorage.setItem('crew-app-locale', locale)
    if (navApp) localStorage.setItem('crew-nav-app', navApp)

    // The persisted graphcache lives in IndexedDB — localStorage.clear() does
    // not touch it, and stale data would rehydrate into the next session.
    try {
      await clearGraphQLCache()
    } catch (error) {
      console.error('Failed to clear persisted graphcache on logout:', error)
    }

    // Detach this operator from OneSignal so they stop receiving push.
    if (Capacitor.isNativePlatform()) {
      OneSignal.logout()
    }
  }, [clearApiAuth, clearGraphQLCache])

  const session = useMemo<Session>(
    () => ({
      loggedIn: isAuthenticated,
      login,
      logout,
    }),
    [isAuthenticated, login, logout],
  )

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  )
}
